import { useEffect, useRef, useState } from 'react';
import { useAdminData } from '../../admin/AdminDataContext';
import { useMapContext } from '../../map/MapContext';
import { aiApi, type AnalyzeResponse, type ChatMessage } from '../../lib/aiApi';
import { ApiError } from '../../lib/api';

const toneClass = {
  summary: { border: 'border-ai-accent', bg: 'bg-ai-accent', text: 'text-ai-accent' },
  risk: { border: 'border-status-danger', bg: 'bg-status-danger', text: 'text-status-danger' },
  forecast: { border: 'border-status-success', bg: 'bg-status-success', text: 'text-status-success' },
  info: { border: 'border-border-default', bg: 'bg-text-secondary', text: 'text-text-secondary' },
};

const insightIcon = { summary: '✦', risk: '!', forecast: '↗', info: 'ℹ' };

export function AiPanel({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { mapSettings } = useAdminData();
  const { aiAreaSelectActive, aiAreaGeometry, startAiAreaSelect, clearAiAreaGeometry } = useMapContext();

  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aiAreaGeometry || !mapSettings?.id) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setAnalysis(null);
    setChatMessages([]);
    setChatError(null);
    aiApi
      .analyze(mapSettings.id, aiAreaGeometry)
      .then(setAnalysis)
      .catch((err) => setAnalyzeError(err instanceof ApiError ? err.message : 'Не удалось получить анализ участка.'))
      .finally(() => setAnalyzing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAreaGeometry, mapSettings?.id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSelectArea = () => {
    setAnalysis(null);
    setAnalyzeError(null);
    clearAiAreaGeometry();
    startAiAreaSelect();
  };

  const handleSendChat = async () => {
    const question = chatInput.trim();
    if (!question || !analysis || chatSending) return;
    const nextMessages: ChatMessage[] = [...chatMessages, { role: 'user', content: question }];
    setChatMessages(nextMessages);
    setChatInput('');
    setChatSending(true);
    setChatError(null);
    try {
      const { reply } = await aiApi.chat(analysis.context, nextMessages);
      setChatMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setChatError(err instanceof ApiError ? err.message : 'Не удалось получить ответ.');
    } finally {
      setChatSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title="ИИ-центр"
        className="absolute right-6 top-6 z-10 flex size-11 items-center justify-center rounded-xl border border-ai-accent bg-bg-surface text-lg text-ai-accent shadow-sm"
      >
        ✦
      </button>
    );
  }

  return (
    // Overlays the map (fixed) instead of sitting in the flex row and pushing
    // it narrower — on a phone-width viewport a 428px flex sibling used to
    // squeeze the map down to nothing (min-w-0 has no other floor to stop at).
    <aside className="fixed inset-y-0 right-0 z-30 flex h-screen w-[min(428px,100vw)] shrink-0 flex-col overflow-y-auto border-l border-border-default bg-bg-surface p-6 shadow-2xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl text-ai-accent">✦</span>
          <h2 className="text-lg font-semibold text-text-primary">ИИ-центр</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-medium text-status-success">● online</span>
          <button
            type="button"
            onClick={onToggle}
            title="Свернуть"
            className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary"
          >
            →
          </button>
        </div>
      </div>
      <p className="mt-2 text-xs text-text-secondary">Контекстный анализ выделенной области</p>

      <button
        type="button"
        onClick={handleSelectArea}
        disabled={aiAreaSelectActive}
        className="mt-4 flex h-10 items-center justify-center rounded-[10px] bg-ai-accent text-[13px] font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {aiAreaSelectActive ? 'Нарисуйте область на карте…' : 'Выделить область'}
      </button>

      <div className="mt-5 flex flex-col gap-4">
        {analyzing ? (
          <p className="text-xs text-text-secondary">Анализируем область…</p>
        ) : analyzeError ? (
          <p className="text-xs font-medium text-status-danger">{analyzeError}</p>
        ) : !analysis ? (
          <p className="text-xs text-text-secondary">
            Нажмите «Выделить область» и обведите участок на карте — ИИ соберёт данные слоёв и OpenStreetMap внутри
            него и даст сводку.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-secondary">
              <span>Площадь: {analysis.area_label}</span>
              {analysis.region && <span>Регион: {analysis.region}</span>}
              <span>Объектов слоёв: {analysis.feature_count}</span>
              <span>Объектов OSM: {analysis.osm_count}</span>
            </div>
            {analysis.insights.map((insight, i) => {
              const tone = toneClass[insight.type] ?? toneClass.info;
              return (
                <div
                  key={`${insight.title}_${i}`}
                  className={`relative overflow-hidden rounded-xl border bg-bg-surface p-4 pl-6 ${tone.border}`}
                >
                  <span className={`absolute left-0 top-3 h-[calc(100%-24px)] w-[5px] rounded-r ${tone.bg}`} />
                  <div className="flex items-center gap-3">
                    <span className={`flex size-8 items-center justify-center rounded-[9px] text-sm text-text-inverse ${tone.bg}`}>
                      {insightIcon[insight.type] ?? insightIcon.info}
                    </span>
                    <p className="text-sm font-semibold text-text-primary">{insight.title}</p>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-text-secondary">{insight.text}</p>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="mt-5 rounded-xl bg-bg-surface-secondary p-4">
        <p className="text-[11px] font-semibold text-text-secondary">Спросите о выделенной области</p>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-border-default bg-bg-surface px-3 py-2.5">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
            placeholder={analysis ? 'Например: как тут с детской инфраструктурой?' : 'Сначала выделите область'}
            disabled={!analysis || chatSending}
            className="flex-1 bg-transparent text-[13px] text-text-primary outline-none placeholder:text-text-secondary disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={handleSendChat}
            disabled={!analysis || !chatInput.trim() || chatSending}
            className="text-lg text-action-primary disabled:cursor-not-allowed disabled:opacity-40"
          >
            ↑
          </button>
        </div>
        {chatError && <p className="mt-2 text-[11px] font-medium text-status-danger">{chatError}</p>}
      </div>

      <div className="mt-5 flex-1 rounded-xl bg-bg-canvas p-4">
        <p className="text-sm font-semibold text-text-primary">Диалог</p>
        <div className="mt-3 flex flex-col gap-3">
          {chatMessages.length === 0 && (
            <p className="text-[11px] text-text-secondary">Ответы ИИ будут здесь.</p>
          )}
          {chatMessages.map((m, i) => (
            <div
              key={i}
              className={`rounded-lg px-3 py-2 text-[11px] leading-relaxed ${
                m.role === 'user'
                  ? 'self-end bg-action-primary/10 text-text-primary'
                  : 'bg-bg-surface text-text-primary'
              }`}
            >
              {m.content}
            </div>
          ))}
          {chatSending && <p className="text-[11px] text-text-secondary">Печатает…</p>}
          <div ref={chatEndRef} />
        </div>
      </div>
    </aside>
  );
}
